interface Step {
  number: string;
  title: string;
  description: string;
  icon: string;
  imageUrl: string;
}

const steps: Step[] = [
  {
    number: '01',
    title: 'We map your current workflow.',
    description:
      'We analyze how leads enter and move through your business today—identifying every gap, delay, and missed touchpoint across your existing stack.',
    icon: 'ri-map-pin-2-line',
    imageUrl:
      'https://public.readdy.ai/ai/img_res/0ca8ea54da8f47a70af9312db6150f7e.jpg',
  },
  {
    number: '02',
    title: 'We build your AI system.',
    description:
      'Our team designs and configures a structured pipeline tailored to your industry, team size, and conversion goals—built for precision, not complexity.',
    icon: 'ri-settings-3-line',
    imageUrl:
      'https://readdy.ai/api/search-image?query=clean%20tech%20software%20development%20build%20configure%20system%20data%20pipeline%20visualization%20abstract%20minimal%20blue%20dark%20modern%20professional%20enterprise%20technology&width=700&height=500&seq=henexa-step2-v2&orientation=landscape',
  },
  {
    number: '03',
    title: 'We deploy and optimize.',
    description:
      'We go live and continuously refine based on real performance data—so your results compound over time with zero extra effort from your team.',
    icon: 'ri-rocket-line',
    imageUrl:
      'https://readdy.ai/api/search-image?query=launch%20deploy%20rocket%20success%20minimal%20clean%20abstract%20glowing%20light%20upward%20growth%20concept%20technology%20enterprise%20professional%20blue%20dark%20background%20minimal&width=700&height=500&seq=henexa-step3-v2&orientation=landscape',
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-white dark:bg-[#070D1B]">
      <div className="max-w-7xl mx-auto px-8 lg:px-16 py-28">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-20">
          <div>
            <span className="text-xs tracking-widest uppercase text-[#2563EB] font-medium block mb-5">
              How It Works
            </span>
            <h2 className="text-5xl lg:text-6xl font-semibold text-[#0B2A4A] dark:text-white tracking-[-0.025em] leading-[1.0]">
              Simple process.<br />Significant results.
            </h2>
          </div>
          <p className="text-sm text-[#0B2A4A]/65 dark:text-white/55 max-w-xs tracking-wide leading-relaxed lg:text-right">
            From initial discovery to live deployment—transparent, structured, and fast.
          </p>
        </div>

        {/* Steps */}
        <div className="flex flex-col gap-5">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`group rounded-2xl overflow-hidden border border-[#E5E7EB] dark:border-white/10 grid grid-cols-1 lg:grid-cols-2 min-h-[320px] hover:border-[#2563EB]/30 dark:hover:border-[#2563EB]/40 transition-all duration-300 ${
                index % 2 === 0 ? '' : 'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1'
              }`}
            >
              {/* Image side */}
              <div className="relative h-56 lg:h-auto overflow-hidden bg-[#F8FAFC] dark:bg-[#0C1322]">
                <img
                  src={step.imageUrl}
                  alt={step.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 dark:to-[#0F1928]/20" />
                {/* Step number overlay */}
                <div className="absolute top-6 left-6 text-8xl font-bold text-white/10 tracking-[-0.05em] leading-none select-none">
                  {step.number}
                </div>
              </div>

              {/* Content side */}
              <div className="p-12 flex flex-col justify-center bg-white dark:bg-[#0F1928]">
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-[#E5E7EB] dark:border-white/20 group-hover:border-[#2563EB]/40 transition-colors">
                    <i className={`${step.icon} text-[#2563EB] text-lg`} />
                  </div>
                  <span className="text-xs text-[#0B2A4A]/60 dark:text-white/50 tracking-widest uppercase font-medium">
                    Step {step.number}
                  </span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-semibold text-[#0B2A4A] dark:text-white tracking-tight leading-snug mb-5">
                  {step.title}
                </h3>
                <p className="text-sm text-[#0B2A4A]/70 dark:text-white/60 leading-relaxed tracking-wide max-w-sm">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-14 pt-8 border-t border-[#E5E7EB] dark:border-white/10 flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm text-[#0B2A4A]/65 dark:text-white/55 tracking-wide">
            Most clients are fully operational within{' '}
            <strong className="text-[#0B2A4A]/80 dark:text-white/75 font-medium">2–3 weeks</strong> of engagement start.
          </p>
          <div className="flex items-center gap-2 text-xs text-[#0B2A4A]/50 dark:text-white/40 tracking-wide">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-check-line text-[#5EEAD4]" />
            </div>
            <span>No long-term lock-in required</span>
          </div>
        </div>
      </div>
    </section>
  );
}
